import './skeleton.css';

export interface SkeletonProps {
  width?: string;
  height?: string;
  radius?: string;
  className?: string;
}

/**
 * A single shimmering placeholder block.
 *
 * The spec is explicit: skeleton rows, never a bare spinner. Anything that
 * loads gets a placeholder shaped like the content that will replace it.
 */
export function Skeleton({ width = '100%', height = '0.75rem', radius, className }: SkeletonProps) {
  return (
    <span
      className={className ? `skeleton ${className}` : 'skeleton'}
      style={{
        width,
        height,
        ...(radius !== undefined ? { borderRadius: radius } : {}),
      }}
      aria-hidden="true"
    />
  );
}

/** Placeholder rows matching the MessageRow layout, used while a folder loads. */
export function MessageListSkeleton({ rows = 12 }: { rows?: number }) {
  return (
    <div className="skeleton-list" role="status" aria-busy="true" aria-label="Loading messages">
      {Array.from({ length: rows }, (_, index) => (
        <div className="skeleton-row" key={index}>
          <Skeleton width="1.9rem" height="1.9rem" radius="var(--radius-pill)" />
          <div className="skeleton-row__lines">
            <div className="skeleton-row__line">
              {/* Varied widths so the placeholder reads as text, not as a table. */}
              <Skeleton width={`${38 + ((index * 13) % 30)}%`} height="0.7rem" />
              <Skeleton width="2.75rem" height="0.6rem" />
            </div>
            <Skeleton width={`${52 + ((index * 17) % 34)}%`} height="0.7rem" />
            <Skeleton width={`${64 + ((index * 11) % 26)}%`} height="0.6rem" />
          </div>
        </div>
      ))}
      <span className="visually-hidden">Loading messages…</span>
    </div>
  );
}

/** Placeholder for the folder tree in the sidebar. */
export function FolderTreeSkeleton({ rows = 7 }: { rows?: number }) {
  return (
    <div className="skeleton-tree" role="status" aria-busy="true" aria-label="Loading folders">
      {Array.from({ length: rows }, (_, index) => (
        <div className="skeleton-tree__row" key={index}>
          <Skeleton width="1rem" height="1rem" radius="var(--radius-sm)" />
          <Skeleton width={`${45 + ((index * 19) % 40)}%`} height="0.7rem" />
        </div>
      ))}
    </div>
  );
}

/** Placeholder for the reading pane. */
export function ReadingPaneSkeleton() {
  return (
    <div className="skeleton-reading" role="status" aria-busy="true" aria-label="Loading message">
      <Skeleton width="60%" height="1.25rem" />
      <div className="skeleton-reading__meta">
        <Skeleton width="2.5rem" height="2.5rem" radius="var(--radius-pill)" />
        <div className="skeleton-reading__meta-lines">
          <Skeleton width="11rem" height="0.75rem" />
          <Skeleton width="16rem" height="0.65rem" />
        </div>
      </div>
      <div className="skeleton-reading__body">
        {['92%', '88%', '95%', '70%', '90%', '84%', '48%'].map((width, index) => (
          <Skeleton key={index} width={width} height="0.7rem" />
        ))}
      </div>
    </div>
  );
}
